import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { z } from "zod";
import { ChoiceSaveQueue, currentPrincipal, principalOf } from "_shared/_api/choice-save-queue";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import type Session from "users/__session/session";
import { serializeQueryArgsBySessionCredential } from "users/__session/session-query-cache-keys";

export const PanelLayoutPreferenceSchema = z.object({
  panelKey: z.string().min(1),
  width: z.number().int().nullish(),
  isCollapsed: z.boolean(),
  updatedAt: z.string(),
});

export const PanelLayoutPreferencesSchema = z.object({
  panels: z.array(PanelLayoutPreferenceSchema),
});

export type PanelLayoutPreferenceJSON = z.infer<typeof PanelLayoutPreferenceSchema>;
export type PanelLayoutPreferencesJSON = z.infer<typeof PanelLayoutPreferencesSchema>;

export type PanelLayoutChoice = {
  /** The width the reader dragged the panel to, or `null` for the panel's own opening width. */
  width: number | null;
  isCollapsed: boolean;
};

type SavePanelLayoutPreferenceArgs = PanelLayoutChoice & {
  session: Session;
  panelKey: string;
};

/** Writes the saved choice into the cached answer, so the next panel drawn reads it without asking. */
function withPanel(draft: PanelLayoutPreferencesJSON, panelKey: string, choice: PanelLayoutChoice): void {
  const panel: PanelLayoutPreferenceJSON = { panelKey, ...choice, updatedAt: new Date().toISOString() };
  const index = draft.panels.findIndex((existing) => existing.panelKey === panelKey);
  if (index >= 0) draft.panels[index] = panel;
  else draft.panels.push(panel);
}

/**
 * The panel saves this tab has asked for and not yet had answered, one queue per panel. A drag then
 * Collapse, or an arrow key held on the separator, sends saves faster than they are answered; one
 * at a time, the account cannot keep an older choice than the page shows (review of #6782).
 */
const panelSaves = new ChoiceSaveQueue<PanelLayoutChoice>();

type RequestAnswer = { data?: unknown; error?: FetchBaseQueryError };

/** The endpoint's base query, as a `queryFn` is handed it. */
type SaveRequest = (args: FetchArgs) => RequestAnswer | PromiseLike<RequestAnswer>;

/** What a save bound to someone who is no longer signed in answers, without being sent. */
const PRINCIPAL_CHANGED: FetchBaseQueryError = {
  status: "CUSTOM_ERROR",
  error: "The person who made this change is no longer the one signed in, so it was not sent.",
};

/**
 * Sends one save in its panel's turn (see `choice-save-queue.ts`).
 *
 * At module level rather than inside the endpoint, so the `try`/`finally` that gives the queue back
 * its place is plain code, whatever the endpoint is compiled into.
 */
async function saveInTurn(
  args: SavePanelLayoutPreferenceArgs,
  send: SaveRequest,
): Promise<{ data: PanelLayoutPreferenceJSON } | { error: FetchBaseQueryError }> {
  const { panelKey, width, isCollapsed } = args;
  const place = panelSaves.join(panelKey, { width, isCollapsed }, principalOf(args.session));
  try {
    await place.turn;
    // A newer save of this panel is waiting behind this one, and it carries the whole choice.
    if (!place.isNewest()) {
      return { data: { panelKey, width, isCollapsed, updatedAt: new Date().toISOString() } };
    }
    if (principalOf(args.session) !== currentPrincipal()) return { error: PRINCIPAL_CHANGED };
    const result = await send({
      url: `users/me/panel-preferences/${encodeURIComponent(panelKey)}`,
      method: "PUT",
      body: { width, isCollapsed },
    });
    return result.error ? { error: result.error } : { data: result.data as PanelLayoutPreferenceJSON };
  } finally {
    place.finish();
  }
}

/** The server's answer, with each choice this person has saved and the server has not yet applied. */
export function withPendingPanelSaves(
  answer: PanelLayoutPreferencesJSON,
  principal: string | undefined,
): PanelLayoutPreferencesJSON {
  const pending = panelSaves.pendingChoices(principal);
  if (pending.length === 0) return answer;
  const draft = { panels: [...answer.panels] };
  for (const [panelKey, choice] of pending) withPanel(draft, panelKey, choice);
  return draft;
}

/** Whether a save of the panel has joined and not finished. */
export function hasPendingPanelSave(panelKey?: string): boolean {
  return panelSaves.hasPendingSave(panelKey);
}

/**
 * How the reader keeps the app's side panels — width and collapsed — kept on their account, so the
 * choice follows them from tab to tab, reload to reload and device to device (#6621).
 */
export const panelLayoutPreferencesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPanelLayoutPreferences: build.query<PanelLayoutPreferencesJSON, { session: Session }>({
      query: ({ session }) => ({
        url: "users/me/panel-preferences",
        headers: { Authorization: `Bearer ${session.authToken?.value}` },
      }),
      serializeQueryArgs: serializeQueryArgsBySessionCredential,
      rawResponseSchema: PanelLayoutPreferencesSchema,
      onSchemaFailure: logSchemaFailure,
      // An answer asked for while a save is on its way can be older than that save.
      transformResponse: (answer: PanelLayoutPreferencesJSON, _meta, { session }) =>
        withPendingPanelSaves(answer, principalOf(session)),
    }),
    savePanelLayoutPreference: build.mutation<PanelLayoutPreferenceJSON, SavePanelLayoutPreferenceArgs>({
      // One at a time per panel, so the account keeps the newest choice (see `saveInTurn`).
      queryFn: (args, _api, _extraOptions, baseQuery) => saveInTurn(args, baseQuery),
      responseSchema: PanelLayoutPreferenceSchema,
      onSchemaFailure: logSchemaFailure,
      // Into the cached answer at once rather than by refetching it: the panel already shows the
      // choice, and every other panel of the same key should open the same way straight away.
      onQueryStarted: ({ session, panelKey, width, isCollapsed }, { dispatch }) => {
        dispatch(
          panelLayoutPreferencesApi.util.updateQueryData("getPanelLayoutPreferences", { session }, (draft) =>
            withPanel(draft, panelKey, { width, isCollapsed }),
          ),
        );
      },
    }),
  }),
});

export const { useGetPanelLayoutPreferencesQuery, useSavePanelLayoutPreferenceMutation } = panelLayoutPreferencesApi;
